<template>
  <section class="home-investment" id="investment">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-6" data-aos="fade-right" data-aos-duration="1000">
          <div class="invest-content">
            <span class="section-tag">
              <i class="ri-funds-line"></i> Franchise Opportunity
            </span>
            <h2>Franchise <span class="text-accent">Investment</span> Overview</h2>
            <p class="lead-text">
              The franchise fee is competitively set at $50,000 with a 10-year term, 
              making this opportunity accessible for a premium quick-service halal Mexican restaurant brand.
            </p>
            <p class="sub-text">
              The halal food market is growing at a 9% CAGR through 2029. Combined with 
              strong demand in the NYC metro area, Guac & Grill is positioned to capture a promising niche 
              with low competition and high consumer demand.
            </p>
            <div class="invest-highlights">
              <div class="highlight-item">
                <div class="highlight-num">9<span>%</span></div>
                <p>CAGR halal food<br/>market growth</p>
              </div>
              <div class="highlight-item">
                <div class="highlight-num">1.39<span>M</span></div>
                <p>Residents in<br/>target market</p>
              </div>
              <div class="highlight-item">
                <div class="highlight-num">$143<span>K</span></div>
                <p>Median household<br/>income</p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6" data-aos="fade-left" data-aos-duration="1000" data-aos-delay="200">
          <div class="fee-cards">
            <div
              v-for="(item, index) in feeItems"
              :key="item.label"
              class="fee-card"
              data-aos="fade-up"
              :data-aos-delay="300 + index * 100"
            >
              <div class="fee-icon">
                <i :class="item.icon"></i>
              </div>
              <div class="fee-info">
                <span class="fee-label">{{ item.label }}</span>
                <span class="fee-value">{{ item.value }}</span>
              </div>
            </div>
            <a href="#contact" class="btn-apply-franchise" @click.prevent="scrollToForm">
              <i class="ri-send-plane-line"></i> Apply for Franchise
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "HomeInvestment",
  setup() {
    const feeItems = [
      { label: "Franchise Term", value: "10 Years", icon: "ri-calendar-check-line" },
      { label: "Franchise Fee", value: "$50,000", icon: "ri-money-dollar-circle-line" },
      { label: "Initial Investment", value: "$250K – $500K", icon: "ri-building-2-line" },
      { label: "Liquid Capital Required", value: "$350,000+", icon: "ri-bank-line" },
    ];

    const scrollToForm = () => {
      const el = document.getElementById('contact');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    return { feeItems, scrollToForm };
  },
});
</script>

<style lang="scss" scoped>
.home-investment {
  padding: 120px 0;
  background: #0f0f0f;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -100px;
    left: -200px;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(181, 255, 0, 0.03) 0%, transparent 70%);
    pointer-events: none;
  }

  .invest-content {
    .section-tag {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #b5ff00;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    h2 {
      font-size: 44px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -1px;
      margin-bottom: 24px;
      line-height: 1.15;
      .text-accent {
        color: #b5ff00;
      }
    }

    .lead-text {
      font-size: 17px;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.7;
      margin-bottom: 16px;
    }

    .sub-text {
      font-size: 15px;
      color: rgba(255, 255, 255, 0.45);
      line-height: 1.7;
      margin-bottom: 36px;
    }

    .invest-highlights {
      display: flex;
      gap: 32px;

      .highlight-item {
        .highlight-num {
          font-size: 36px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
          margin-bottom: 6px;
          span {
            font-size: 20px;
            color: #b5ff00;
          }
        }
        p {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.4);
          margin: 0;
          line-height: 1.4;
        }
      }
    }
  }

  .fee-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .fee-card {
    display: flex;
    align-items: center;
    gap: 20px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 24px 28px;
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(181, 255, 0, 0.2);
      transform: translateY(-3px);
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
      background: rgba(255, 255, 255, 0.06);
    }

    .fee-icon {
      width: 48px;
      height: 48px;
      min-width: 48px;
      background: rgba(181, 255, 0, 0.1);
      border: 1px solid rgba(181, 255, 0, 0.15);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      i {
        font-size: 22px;
        color: #b5ff00;
      }
    }

    .fee-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex: 1;

      .fee-label {
        font-size: 15px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.7);
      }

      .fee-value {
        font-size: 18px;
        font-weight: 800;
        color: #ffffff;
        background: rgba(181, 255, 0, 0.08);
        border: 1px solid rgba(181, 255, 0, 0.15);
        padding: 6px 16px;
        border-radius: 8px;
      }
    }
  }

  .btn-apply-franchise {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #b5ff00;
    color: #0a0a0a;
    padding: 18px 36px;
    border-radius: 60px;
    font-size: 16px;
    font-weight: 700;
    text-decoration: none;
    transition: all 0.3s ease;
    margin-top: 8px;
    border: 2px solid #b5ff00;

    &:hover {
      background: transparent;
      color: #b5ff00;
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(181, 255, 0, 0.25);
    }
  }
}

@media (max-width: 991px) {
  .home-investment {
    padding: 80px 0;

    .invest-content {
      margin-bottom: 48px;

      h2 {
        font-size: 34px;
      }

      .invest-highlights {
        gap: 20px;
        .highlight-item .highlight-num {
          font-size: 28px;
        }
      }
    }

    .fee-card .fee-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }
  }
}
</style>
